import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { fetchSeatmap, fetchSeatmapBackground } from '@/api/seatmap';
import { useCartStore } from '@/stores/cart';
import { useCheckoutStore } from '@/stores/checkoutStore';
import { useToastStore } from '@/stores/toastStore';
import type { CartItem, PriceGroupInfo, SeatmapData, SeatmapQueueItem, SeatPlacementConfig, SeatPriceGroupData, SeatState } from '@/types';
import { checkForHoles } from '@/utils/seatmap/checkForInvalidSeatPlacement';
import { useWebsocketStore } from '@/websocket/websocket';

export const useSeatmapStore = defineStore('seatmap', () => {
    const cachedSeatmaps = ref<Record<string, SeatmapData>>({});
    const loadedSeatmaps = ref<Record<string, boolean>>({});
    const loading = ref(false);
    const loadQueue: string[] = [];
    // FIFO queue correlating outgoing reserveseats requests to their responses.
    // The server does not echo the seatmap name back, so we track it ourselves.
    const pendingReserveSeats: string[] = [];

    // Queue of seatmaps the customer must fill for the current checkout
    const seatmapQueue = ref<SeatmapQueueItem[]>([]);
    const currentSeatmapIndex = ref(0);

    // Per-seatmap selection state driven by WebSocket responses
    const seatStates = ref<Record<string, SeatState>>({});

    // User's explicit seat→ticket assignments.
    // Keyed as: seatTicketAssignments[seatmapName][seatIndex] → ticketId
    const seatTicketAssignments = ref<Record<string, Record<number, number>>>({});

    // Per-event snapshots of completed seat selections. When the user finishes
    // selecting seats for an event and transitions to the next, the current
    // queue/states/assignments are snapshotted here so buildAllPriceGroupInfo
    // can aggregate all events at orderdata/placeorder time.
    interface CompletedEventSeatSnapshot {
        queue: SeatmapQueueItem[];
        seatStates: Record<string, SeatState>;
        seatTicketAssignments: Record<string, Record<number, number>>;
    }
    const completedEventSeatData = ref<Record<string | number, CompletedEventSeatSnapshot>>({});

    // Restore completed event snapshots from localStorage on init
    (function restoreCompletedEventsOnInit() {
        try {
            const raw = localStorage.getItem('seatmapCompletedEvents');
            if (!raw) return;
            completedEventSeatData.value = JSON.parse(raw);
        } catch { /* ignore corrupt data */ }
    })();

    // Per-event seat snapshots for the doorsale flow. Intentionally NOT cleared
    // by clearSeatmapState() so backwards navigation to a previously confirmed
    // doorsale event restores its seats without releasing the server-side
    // reservation. Only the currently-active event's snapshot is invalidated when
    // ticket counts change. All snapshots are cleared on full cart reset.
    interface DoorsaleEventSeatSnapshot {
        seatsOurs: Record<string, number[]>;
        seatTicketAssignments: Record<string, Record<number, number>>;
        priceGroups?: Record<string, Record<string, Record<string, SeatPriceGroupData>> | undefined>;
    }
    const doorsaleEventSnapshots = ref<Record<string | number, DoorsaleEventSeatSnapshot>>({});

    // null = idle, true = validation passed, false = validation failed
    const validatePriceGroupsOk = ref<boolean | null>(null);

    // Event-level max pixel distance for seat-spacing enforcement (default 25)
    const seatmapMaxDistance = ref(25);

    // Track which events in the basket have seatmaps and which have completed
    // seat selection (for multi-event baskets).
    const eventsWithSeatmaps = ref<Record<string | number, true>>({});
    const eventsWithSeatsCompleted = ref<Record<string | number, true>>({});

    // Restore event seatmap registry from localStorage on store init
    // so multi-event tracking survives page reloads.
    (function restoreRegistryOnInit() {
        try {
            const raw = localStorage.getItem('seatmapEventsRegistry');
            if (!raw) return;
            const data = JSON.parse(raw) as { eventsWithSeatmaps: (string | number)[] };
            for (const id of data.eventsWithSeatmaps) {
                eventsWithSeatmaps.value[id] = true;
            }
        } catch { /* ignore corrupt data */ }
    })();

    function setMaxDistance(distance: number) {
        seatmapMaxDistance.value = distance;
    }

    function registerEventWithSeatmaps(eventId: number | string) {
        eventsWithSeatmaps.value[eventId] = true;
        saveEventSeatmapRegistry();
    }

    function markEventSeatsCompleted(eventId: number | string) {
        eventsWithSeatsCompleted.value[eventId] = true;
    }

    /**
     * Returns the first cart item ID that has seatmaps but hasn't completed
     * seat selection yet, or undefined if all events are done.
     */
    function getNextEventNeedingSeats(cartItems: CartItem[]): number | string | undefined {
        for (const item of cartItems) {
            if (eventsWithSeatmaps.value[item.id] && !eventsWithSeatsCompleted.value[item.id]) {
                return item.id;
            }
        }
        return undefined;
    }

    // ── Computed ──────────────────────────────────────────────────────────────

    const currentQueueItem = computed<SeatmapQueueItem | undefined>(
        () => seatmapQueue.value[currentSeatmapIndex.value]
    );

    const allSeatsSelected = computed(() =>
        seatmapQueue.value.every(item => {
            const state = seatStates.value[item.name];
            return state && state.seatsOurs.length == item.count;
        })
    );

    const { t: $t } = useI18n();

    // ── Asset loading ─────────────────────────────────────────────────────────

    async function loadSeatmap(name: string) {
        if (loadedSeatmaps.value[name]) {
            const next = loadQueue.shift();
            if (next) loadSeatmap(next);
            return;
        }
        if (loading.value) {
            if (!loadQueue.includes(name)) loadQueue.push(name);
            return;
        }

        loading.value = true;

        const seatmapData = await fetchSeatmap(name);
        if (seatmapData) {
            cachedSeatmaps.value[name] = seatmapData;
        }

        const backgroundUrl = await fetchSeatmapBackground(name);
        if (backgroundUrl && cachedSeatmaps.value[name]) {
            cachedSeatmaps.value[name].backgroundUrl = backgroundUrl;
        }

        loadedSeatmaps.value[name] = true;
        loading.value = false;

        const next = loadQueue.shift();
        if (next) loadSeatmap(next);
    }

    // ── Queue management ──────────────────────────────────────────────────────

    function advanceToNextSeatmap() {
        if (currentSeatmapIndex.value < seatmapQueue.value.length - 1) {
            currentSeatmapIndex.value++;
        }
    }

    // ── WebSocket actions ─────────────────────────────────────────────────────

    /**
     * Ask the server which seatmaps are needed for this basket and how many
     * seats are required on each. The server cross-references the session basket
     * against the event definition and responds with the authoritative list.
     */
    function sendEventseatmaps(eventId: number | string) {
        const websocketStore = useWebsocketStore();
        websocketStore.send('eventseatmaps', { eventid: eventId }, undefined);
    }

    function sendReserveSeats(name: string, seatIndices: number[], clear = false) {
        const websocketStore = useWebsocketStore();
        const checkoutStore = useCheckoutStore();
        websocketStore.send('reserveseats', {
            eventid: checkoutStore.getCheckoutID(),
            seatmap: name,
            seats: seatIndices,
            empty: clear ? true : false,
        }, undefined);
        pendingReserveSeats.push(name);
    }

    function resetCurrentSeatmap() {
        const item = currentQueueItem.value;
        if (!item) return;
        delete seatTicketAssignments.value[item.name];
        sendReserveSeats(item.name, [], true);
    }

    /**
     * Validates that the proposed seat selection does not strand a single isolated
     * free seat beside the selection (which could never be sold). If the placement
     * is auto-correctable (exactly one stranded seat on one side) the selection is
     * shifted one step to absorb the gap; if it cannot be corrected the selection
     * is rejected and a toast is shown.
     *
     * Calls sendReserveSeats internally on success.
     */
    function validateAndSendReserveSeats(name: string, proposedSeats: number[]): void {
        const seatmapData = cachedSeatmaps.value[name];
        if (!seatmapData) {
            // Seatmap data not yet available — cannot validate, do not send
            return;
        }

        const queueItem = seatmapQueue.value.find(item => item.name == name);
        if (!queueItem) {
            return;
        }

        const seatState  = seatStates.value[name];
        const state2     = seatState?.state2 ?? [];

        // Build taken dict: seats occupied by others (state2 > 0 and not in proposedSeats)
        const proposedSet = new Set(proposedSeats);
        const taken: Record<number, 1> = {};
        for (let i = 0; i < state2.length; i++) {
            if ((state2[i] ?? 0) > 0 && !proposedSet.has(i)) {
                taken[i] = 1;
            }
        }

        const buildOurs = (seats: number[]): Record<number, 1> => {
            const o: Record<number, 1> = {};
            for (const seatIndex of seats) o[seatIndex] = 1;
            return o;
        };

        const placementConfig: SeatPlacementConfig = {
            maxDistance:                     seatmapMaxDistance.value,
            checkHolesOverride:              queueItem.checkHolesOverride,
            forceSeatSpace:                  queueItem.forceSeatSpace,
            forceEdgeSeatSelection:          queueItem.forceEdgeSeatSelection,
            forceEdgeSeatSelectionDirection: queueItem.forceEdgeSeatSelectionDirection,
        };

        const result = checkForHoles(taken, buildOurs(proposedSeats), seatmapData, placementConfig);

        let ok = true;

        if (result.no_good_placement || !result.placement_ok) {
            ok = false;
        } 

        /*if (!ok) {
            const toastStore = useToastStore();
            toastStore.showToast($t('checkout.seat_invalid_placement'));
            return;
        }*/

        // We wish to get the seats left so that we only stop reservation if there is a single seat selection left and the user must close a hole.

        const neededCount = currentQueueItem.value?.count ?? 0;

        if(proposedSeats.length >= neededCount && !ok) {
            const toastStore = useToastStore();
            console.log('Invalid seat placement detected. Result:', result);
            if(result.mustSelectFromRowError) {
                toastStore.showToast($t('checkout.seat_invalid_placement_select_from_row'));
            }
            else {
                toastStore.showToast($t('checkout.seat_invalid_placement'));
            }
            return;
        }
        else {
            sendReserveSeats(name, proposedSeats);
        }
    }

    // ── WebSocket response handlers ───────────────────────────────────────────

    /**
     * The server responds with the authoritative list of seatmaps needed for
     * the current basket. Populate the queue, initialise seat states, kick off
     * asset loading, and request initial seat availability (state2) for each map.
     */
    function handleEventseatmapsResponse(data: {
        seatmaps: Array<{
            name: string;
            count: number;
        }>;
        check_holes?: number | null;
        force_seat_space?: number;
        force_edge_seat_selection?: number;
        force_edge_seat_selection_direction?: number;
    }) {
        if (!data.seatmaps) return;
        const checkoutStore = useCheckoutStore();
        console.log('Received eventseatmaps response for event', checkoutStore.getCheckoutID(), 'with seatmaps:', data.seatmaps);

        // Discard any stale entries from a previous mount cycle. Receiving
        // eventseatmaps signals the start of a fresh reserveseats round-trip
        // sequence; leftover entries would mis-map responses to the wrong seatmaps.
        pendingReserveSeats.length = 0;

        // Load any previously stored queue so we can fall back to its ticketIds
        // when checkoutStore.tickets hasn't been populated yet (race condition on reload).
        // Guard: only use stored state if it belongs to the same event. When two
        // events share the same seatmap name, stale seats from a previous event
        // must not bleed into the new event's session.
        const rawStoredState = loadSeatmapStateFromStorage();
        const storedState = rawStoredState && String(rawStoredState.eventId) == String(checkoutStore.getCheckoutID())
            ? rawStoredState
            : null;

        // Doorsale-specific snapshot: survives clearSeatmapState() between events
        // so backwards navigation can re-confirm a previously selected event's seats.
        // In the checkout flow this is always null (saveDoorsaleEventSnapshot is never
        // called from checkout views), so it has no effect on that flow.
        const doorsaleSnap = loadDoorsaleEventSnapshot(checkoutStore.getCheckoutID() ?? '');

        // Build typed queue from server-provided names + counts.
        // Look up which local ticket IDs map to each seatmap so the view
        // can display per-seat prices.
        const queue: SeatmapQueueItem[] = data.seatmaps.map(seatmap => {
            let ticketIds = checkoutStore.tickets
                .filter(ticket => ticket.seatmap == seatmap.name)
                .map(ticket => ticket.id);

            // Race-condition fallback: if checkoutStore.tickets hasn't arrived yet,
            // use ticketIds from the stored queue item for this seatmap.
            if (ticketIds.length == 0 && storedState) {
                const storedItem = storedState.queue.find(q => q.name == seatmap.name);
                if (storedItem && storedItem.ticketIds.length > 0) {
                    ticketIds = storedItem.ticketIds;
                }
            }

            // Last-resort fallback: derive ticketIds from existing seat-ticket
            // assignments. This breaks the self-reinforcing cycle where empty
            // ticketIds get persisted and re-loaded on every subsequent visit.
            if (ticketIds.length == 0) {
                const assignments = seatTicketAssignments.value[seatmap.name] ??
                    storedState?.seatTicketAssignments?.[seatmap.name] ??
                    doorsaleSnap?.seatTicketAssignments?.[seatmap.name] ?? {};
                const derivedIds = [...new Set(Object.values(assignments).map(Number))].filter(id => id > 0);
                if (derivedIds.length > 0) {
                    ticketIds = derivedIds;
                }
            }
            console.log('Mapped seatmap', seatmap.name, 'to ticket IDs', ticketIds);
            console.log('Seatmap config - checkHoles:', data.check_holes, 'forceSeatSpace:', data.force_seat_space, 'forceEdgeSeatSelection:', data.force_edge_seat_selection, 'forceEdgeSeatSelectionDirection:', data.force_edge_seat_selection_direction);

            return {
                name: seatmap.name,
                count: seatmap.count,
                ticketIds,
                checkHolesOverride:              data.check_holes == null ? null : !!data.check_holes,
                forceSeatSpace:                  !!data.force_seat_space,
                forceEdgeSeatSelection:          !!data.force_edge_seat_selection,
                forceEdgeSeatSelectionDirection: data.force_edge_seat_selection_direction,
            };
        });

        seatmapQueue.value = queue;
        // Restore the previously active seatmap index if we are replaying an existing
        // session (i.e. the queue names match), otherwise start from 0.
        if (storedState && storedState.queue.length == queue.length &&
            storedState.queue.every((s, i) => s.name == queue[i]?.name)) {
            currentSeatmapIndex.value = storedState.currentSeatmapIndex ?? 0;
        } else {
            currentSeatmapIndex.value = 0;
        }

        // Initialise seat states. Use stored seatsOurs and priceGroups as a
        // placeholder so isCurrentMapComplete stays true and seat→ticket
        // assignments resolve on reload while waiting for the authoritative
        // reserveseats WS response that will replace this entirely.
        const freshStates: Record<string, SeatState> = {};
        for (const item of queue) {
            freshStates[item.name] = {
                state2: [],
                seatsOurs: storedState?.seatsOurs?.[item.name] ?? doorsaleSnap?.seatsOurs?.[item.name] ?? [],
                colors: [],
                overrideColors: [],
                priceGroups: storedState?.priceGroups?.[item.name],
            };
        }
        seatStates.value = freshStates;

        // Load background + definition for every map in the queue
        for (const item of queue) {
            loadSeatmap(item.name);
        }

        // Request initial seat availability (state2) for every map.
        // If seats were previously selected (stored in localStorage), resend them
        // so the server re-confirms the reservation and echoes them back in data.seats.
        // Sending an empty array would cause the server to return seats: [] and wipe
        // the displayed selection when navigating back from a later checkout step.
        // When no stored seats exist, pass empty=true so the server still returns
        // price_groups for initial seat colouring.
        for (const item of queue) {
            const storedSeats = storedState?.seatsOurs?.[item.name] ?? doorsaleSnap?.seatsOurs?.[item.name] ?? [];
            sendReserveSeats(item.name, storedSeats, storedSeats.length == 0);
        }
    }

    function handleReserveSeatsResponse(data: {
        ok: boolean;
        state: unknown;
        state2: number[];
        override_colors: string[];
        colors: number[];
        price_groups: unknown;
        seats: number[];
    }) {
        // The server does not include the seatmap name in the response; use the FIFO
        // queue populated by sendReserveSeats to resolve which map this belongs to.
        // Fall back to the current queue item when the FIFO is unexpectedly empty
        // (e.g. cleared by a concurrent eventseatmaps cycle) so user-initiated
        // reserveseats responses are not silently dropped.
        const name = pendingReserveSeats.shift() ?? currentQueueItem.value?.name;
        if (!name) return;
        // Preserve previous priceGroups when the new response doesn't include them.
        // The server only populates price_groups when seats are non-empty or empty=true;
        // keeping the last known value ensures seat colouring persists across redraws.
        const previousPriceGroups = seatStates.value[name]?.priceGroups;
        const newPriceGroups = data.price_groups as Record<string, Record<string, SeatPriceGroupData>> | undefined;
        // Replace the whole object atomically so Vue fires exactly one reactive update,
        // which guarantees the canvas watcher sees a reference change and redraws once.
        seatStates.value[name] = {
            state2: data.state2 ?? [],
            seatsOurs: (data.seats ?? []).map(Number),
            colors: data.colors ?? [],
            overrideColors: data.override_colors ?? [],
            priceGroups: newPriceGroups ?? previousPriceGroups,
        };
        // Auto-assign tickets to seats when price groups are present.
        autoAssignSeatsForMap(name);

        // ok: false means the server rejected the reservation (e.g. seat became taken).
        // State is still applied above so the canvas reflects reality; show a toast.
        if (!data.ok) {
            const toastStore = useToastStore();
            toastStore.showToast($t('checkout.seat_reservation_failed'));
        }
    }

    // ── Label helper ──────────────────────────────────────────────────────────

    /**
     * Returns the price-group data for a specific seat, or undefined if the seat
     * has no price group (or price groups haven't been received yet).
     */
    function getSeatPriceGroup(
        seatIndex: number,
        name: string,
    ): SeatPriceGroupData | undefined {
        const priceGroups = seatStates.value[name]?.priceGroups;
        if (!priceGroups) return undefined;
        for (const ticketGroups of Object.values(priceGroups)) {
            for (const group of Object.values(ticketGroups)) {
                if (group.seats[seatIndex]) return group;
            }
        }
        return undefined;
    }

    /**
     * Returns all eligible { ticketId, group } pairs for a given seat — i.e.
     * every ticket whose price groups contain this seat index.
     */
    function getEligibleTicketsForSeat(
        seatIndex: number,
        name: string,
    ): Array<{ ticketId: number; group: SeatPriceGroupData }> {
        const priceGroups = seatStates.value[name]?.priceGroups;
        if (!priceGroups) return [];
        const result: Array<{ ticketId: number; group: SeatPriceGroupData }> = [];
        for (const [ticketIdStr, ticketGroups] of Object.entries(priceGroups)) {
            for (const group of Object.values(ticketGroups)) {
                if (group.seats[seatIndex]) {
                    result.push({ ticketId: Number(ticketIdStr), group });
                    break; // one match per ticket is enough
                }
            }
        }
        return result;
    }

    /**
     * Like getSeatPriceGroup but respects the user's explicit seat→ticket
     * assignment. Falls back to getSeatPriceGroup when no assignment exists.
     */
    function getAssignedSeatPriceGroup(
        seatIndex: number,
        name: string,
    ): SeatPriceGroupData | undefined {
        const assignedTicketId = seatTicketAssignments.value[name]?.[seatIndex];
        if (assignedTicketId != null) {
            const priceGroups = seatStates.value[name]?.priceGroups;
            if (priceGroups) {
                const ticketGroups = priceGroups[String(assignedTicketId)];
                if (ticketGroups) {
                    for (const group of Object.values(ticketGroups)) {
                        if (group.seats[seatIndex]) return group;
                    }
                }
            }
        }
        return getSeatPriceGroup(seatIndex, name);
    }

    /**
     * Assign a ticket to a seat. Performs auto-swap: if another seat currently
     * holds the new ticket and is eligible for the old ticket, swap them so
     * ticket counts stay balanced.
     */
    function assignTicketToSeat(seatIndex: number, newTicketId: number, name: string) {
        const assignments = seatTicketAssignments.value[name];
        if (!assignments) return;

        const oldTicketId = assignments[seatIndex];
        if (oldTicketId == newTicketId) return;

        // Find a swap candidate: another seat assigned to newTicketId that is
        // eligible for oldTicketId.
        const seatsOurs = seatStates.value[name]?.seatsOurs ?? [];
        let swapSeat: number | null = null;
        for (const otherSeat of seatsOurs) {
            if (otherSeat == seatIndex) continue;
            if (assignments[otherSeat] != newTicketId) continue;
            // Check if the other seat is eligible for the old ticket
            const eligible = getEligibleTicketsForSeat(otherSeat, name);
            if (eligible.some(e => e.ticketId == oldTicketId)) {
                swapSeat = otherSeat;
                break;
            }
        }

        // Apply assignment (and swap)
        assignments[seatIndex] = newTicketId;
        if (swapSeat != null && oldTicketId != null) {
            assignments[swapSeat] = oldTicketId;
        }
    }

    /**
     * Swap the ticket assignments of two seats directly. Used by the view when
     * the user picks a different seat for a ticket via the seat-picker dropdown.
     */
    function swapSeatAssignments(seatA: number, seatB: number, name: string) {
        const assignments = seatTicketAssignments.value[name];
        if (!assignments) return;
        const ticketA = assignments[seatA];
        const ticketB = assignments[seatB];
        if (ticketA != null) assignments[seatB] = ticketA;
        if (ticketB != null) assignments[seatA] = ticketB;
    }

    /**
     * Auto-assign tickets to seats when seatsOurs changes. Distributes ticket
     * types in ticketIds order, respecting cart counts and price-group
     * eligibility.
     */
    function autoAssignSeatsForMap(name: string) {
        const queueItem = seatmapQueue.value.find(item => item.name == name);
        if (!queueItem) return;

        const seatsOurs = seatStates.value[name]?.seatsOurs ?? [];
        const priceGroups = seatStates.value[name]?.priceGroups;
        if (!priceGroups || Object.keys(priceGroups).length == 0) return;

        const checkoutStore = useCheckoutStore();
        const cartStore = useCartStore();
        const cartItem = cartStore.items.find(ci => String(ci.id) == String(checkoutStore.getCheckoutID()));

        // Build budget: how many seats each ticket type needs
        const budget: Record<number, number> = {};
        for (const ticketId of queueItem.ticketIds) {
            const cartTicket = cartItem?.tickets.find(t => t.id == ticketId);
            if (cartTicket) budget[ticketId] = (budget[ticketId] ?? 0) + cartTicket.count;
        }

        // Preserve existing valid assignments, but only for seats that are
        // still in seatsOurs. Stale entries from previously selected (now
        // deselected) seats must be discarded so the guard below doesn't
        // incorrectly prevent fresh assignments from being written.
        const raw = seatTicketAssignments.value[name] ?? {};
        const seatsOursSet = new Set(seatsOurs);
        const existing: Record<number, number> = {};
        for (const [seatStr, ticketId] of Object.entries(raw)) {
            const seat = Number(seatStr);
            if (seatsOursSet.has(seat)) {
                existing[seat] = ticketId;
            }
        }
        const newAssignments: Record<number, number> = {};
        const used: Record<number, number> = {}; // ticketId → count assigned so far

        // First pass: keep valid existing assignments
        for (const seat of seatsOurs) {
            const prev = existing[seat];
            if (prev != null && (budget[prev] ?? 0) > (used[prev] ?? 0)) {
                // Verify the seat is still eligible for this ticket
                const eligible = getEligibleTicketsForSeat(seat, name);
                if (eligible.some(e => e.ticketId == prev)) {
                    newAssignments[seat] = prev;
                    used[prev] = (used[prev] ?? 0) + 1;
                }
            }
        }

        // Second pass: assign unassigned seats
        for (const seat of seatsOurs) {
            if (newAssignments[seat] != null) continue;
            const eligible = getEligibleTicketsForSeat(seat, name);
            for (const ticketId of queueItem.ticketIds) {
                if ((used[ticketId] ?? 0) >= (budget[ticketId] ?? 0)) continue;
                if (eligible.some(e => e.ticketId == ticketId)) {
                    newAssignments[seat] = ticketId;
                    used[ticketId] = (used[ticketId] ?? 0) + 1;
                    break;
                }
            }
        }

        // Guard: don't overwrite existing valid assignments with an empty set.
        // This can happen when ticketIds is empty (corrupted queue) causing
        // the budget to be empty — which would wipe the sidebar seat grouping.
        if (Object.keys(newAssignments).length == 0 && Object.keys(existing).length > 0 && seatsOurs.length > 0) {
            return;
        }

        seatTicketAssignments.value[name] = newAssignments;
    }

    /**
     * Sends a validatepricegroups message to the server covering all seatmaps in
     * the current queue. Builds price_group_info from the confirmed seat selections
     * and the price-group data stored after each reserveseats response.
     */
    /**
     * Build the price_group_info payload for a single event from LIVE state.
     * Used by validatepricegroups (which validates the current event only).
     */
    function buildPriceGroupInfo(eventId: number | string): PriceGroupInfo | undefined {
        // Shape expected by backend:
        // { [eventId]: { [seatmapName]: { [seatIndex]: { ticket_id, price_group, price, event_id } } } }
        const seatmapInfo: Record<string, Record<string, { ticket_id: number; price_group: number; price: number; event_id: number | string }>> = {};

        for (const queueItem of seatmapQueue.value) {
            const seatsOurs = seatStates.value[queueItem.name]?.seatsOurs ?? [];
            if (seatsOurs.length == 0) continue;

            for (const seatIndex of seatsOurs) {
                const group = getAssignedSeatPriceGroup(seatIndex, queueItem.name);
                if (!group) continue;
                if (!seatmapInfo[queueItem.name]) seatmapInfo[queueItem.name] = {};
                seatmapInfo[queueItem.name][seatIndex] = {
                    ticket_id: group.ticket_id,
                    price_group: group.id,
                    price: group.price,
                    event_id: group.event_id,
                };
            }
        }

        if (Object.keys(seatmapInfo).length == 0) return undefined;
        return { [eventId]: seatmapInfo };
    }

    /**
     * Build the price_group_info payload for ALL events — aggregates completed
     * event snapshots plus the current live event. Used by orderdata/placeorder.
     */
    function buildAllPriceGroupInfo(): PriceGroupInfo | undefined {
        const result: PriceGroupInfo = {};

        // 1. Build from completed event snapshots
        for (const [eventIdStr, snapshot] of Object.entries(completedEventSeatData.value)) {
            const seatmapInfo: Record<string, Record<string, { ticket_id: number; price_group: number; price: number; event_id: number | string }>> = {};

            for (const queueItem of snapshot.queue) {
                const seatsOurs = snapshot.seatStates[queueItem.name]?.seatsOurs ?? [];
                if (seatsOurs.length == 0) continue;

                for (const seatIndex of seatsOurs) {
                    // Resolve price group from the snapshot's assignments and state
                    const assignedTicketId = snapshot.seatTicketAssignments[queueItem.name]?.[seatIndex];
                    let group: SeatPriceGroupData | undefined;
                    const priceGroups = snapshot.seatStates[queueItem.name]?.priceGroups;
                    if (assignedTicketId != null && priceGroups) {
                        const ticketGroups = priceGroups[String(assignedTicketId)];
                        if (ticketGroups) {
                            for (const g of Object.values(ticketGroups)) {
                                if (g.seats[seatIndex]) { group = g; break; }
                            }
                        }
                    }
                    if (!group && priceGroups) {
                        for (const ticketGroups of Object.values(priceGroups)) {
                            for (const g of Object.values(ticketGroups)) {
                                if (g.seats[seatIndex]) { group = g; break; }
                            }
                            if (group) break;
                        }
                    }
                    if (!group) continue;
                    if (!seatmapInfo[queueItem.name]) seatmapInfo[queueItem.name] = {};
                    seatmapInfo[queueItem.name][seatIndex] = {
                        ticket_id: group.ticket_id,
                        price_group: group.id,
                        price: group.price,
                        event_id: group.event_id,
                    };
                }
            }

            if (Object.keys(seatmapInfo).length > 0) {
                result[eventIdStr] = seatmapInfo;
            }
        }

        // 2. For completed doorsale events whose completedEventSeatData entry was wiped
        // by clearSeatmapState (triggered when adding tickets for a subsequent event),
        // fall back to the doorsale snapshot which survives clearSeatmapState for all
        // events except the currently-active one.
        for (const [eventIdStr, snap] of Object.entries(doorsaleEventSnapshots.value)) {
            if (result[eventIdStr]) continue; // already covered by completedEventSeatData
            if (!snap.priceGroups) continue;
            const seatmapInfo: Record<string, Record<string, { ticket_id: number; price_group: number; price: number; event_id: number | string }>> = {};
            for (const [smName, pg] of Object.entries(snap.priceGroups)) {
                if (!pg) continue;
                const seatsOurs = snap.seatsOurs[smName] ?? [];
                if (seatsOurs.length == 0) continue;
                for (const seatIndex of seatsOurs) {
                    const assignedTicketId = snap.seatTicketAssignments[smName]?.[seatIndex];
                    let group: SeatPriceGroupData | undefined;
                    if (assignedTicketId != null) {
                        const ticketGroups = pg[String(assignedTicketId)];
                        if (ticketGroups) {
                            for (const g of Object.values(ticketGroups)) {
                                if (g.seats[seatIndex]) { group = g; break; }
                            }
                        }
                    }
                    if (!group) {
                        for (const ticketGroups of Object.values(pg)) {
                            for (const g of Object.values(ticketGroups)) {
                                if (g.seats[seatIndex]) { group = g; break; }
                            }
                            if (group) break;
                        }
                    }
                    if (!group) continue;
                    if (!seatmapInfo[smName]) seatmapInfo[smName] = {};
                    seatmapInfo[smName][seatIndex] = {
                        ticket_id: group.ticket_id,
                        price_group: group.id,
                        price: group.price,
                        event_id: group.event_id,
                    };
                }
            }
            if (Object.keys(seatmapInfo).length > 0) {
                result[eventIdStr] = seatmapInfo;
            }
        }

        // 3. Build from current live state (for the active event, if not already snapshotted)
        const checkoutStore = useCheckoutStore();
        const currentEventId = checkoutStore.getCheckoutID();
        if (currentEventId != null && !completedEventSeatData.value[currentEventId] && !result[currentEventId]) {
            const liveInfo = buildPriceGroupInfo(currentEventId);
            if (liveInfo) {
                Object.assign(result, liveInfo);
            }
        }

        return Object.keys(result).length > 0 ? result : undefined;
    }

    /**
     * Snapshot the current event's seatmap state into completedEventSeatData.
     * Called when the user finishes seat selection for an event, before
     * transitioning to the next event or the next checkout step.
     */
    function snapshotCompletedEvent(eventId: number | string) {
        completedEventSeatData.value[eventId] = {
            queue: JSON.parse(JSON.stringify(seatmapQueue.value)),
            seatStates: JSON.parse(JSON.stringify(seatStates.value)),
            seatTicketAssignments: JSON.parse(JSON.stringify(seatTicketAssignments.value)),
        };
        persistCompletedEvents();
    }

    function persistCompletedEvents() {
        try {
            localStorage.setItem('seatmapCompletedEvents', JSON.stringify(completedEventSeatData.value));
        } catch { /* quota exceeded — non-critical */ }
    }

    function sendValidatePriceGroups(eventId: number) {
        const websocketStore = useWebsocketStore();

        const priceGroupInfo = buildPriceGroupInfo(eventId);

        // Nothing to validate — skip the round-trip
        if (!priceGroupInfo) {
            validatePriceGroupsOk.value = true;
            return;
        }

        validatePriceGroupsOk.value = null;
        websocketStore.send('validatepricegroups', {
            eventid: eventId,
            price_group_info: priceGroupInfo,
        }, undefined);
    }

    function handleValidatePriceGroupsResponse(data: { ok: boolean | number }) {
        validatePriceGroupsOk.value = !!data.ok;
    }

    // ── Persistence ───────────────────────────────────────────────────────────

    interface StoredSeatmapState {
        eventId: number | string;
        queue: SeatmapQueueItem[];
        currentSeatmapIndex: number;
        seatTicketAssignments: Record<string, Record<number, number>>;
        // Per-seatmap selected seat indices — enables the Continue button immediately
        // on reload before the authoritative WS reserveseats response arrives.
        seatsOurs: Record<string, number[]>;
        // Per-seatmap price-group data so seat→ticket assignment and per-seat
        // pricing survive navigation without waiting for the WS response.
        priceGroups?: Record<string, Record<string, Record<string, SeatPriceGroupData>> | undefined>;
    }

    function storeSeatmapState(eventId: number | string) {
        if (seatmapQueue.value.length == 0) return;

        // Recover empty ticketIds from seatTicketAssignments before persisting.
        // This prevents a corrupted queue (with empty ticketIds) from being
        // saved and creating a self-reinforcing bug on subsequent reloads.
        const queue = seatmapQueue.value.map(item => {
            if (item.ticketIds.length > 0) return item;
            const assignments = seatTicketAssignments.value[item.name] ?? {};
            const derivedIds = [...new Set(Object.values(assignments).map(Number))].filter(id => id > 0);
            return derivedIds.length > 0 ? { ...item, ticketIds: derivedIds } : item;
        });

        const state: StoredSeatmapState = {
            eventId,
            queue,
            currentSeatmapIndex: currentSeatmapIndex.value,
            seatTicketAssignments: seatTicketAssignments.value,
            seatsOurs: Object.fromEntries(
                Object.entries(seatStates.value).map(([name, s]) => [name, s.seatsOurs ?? []]),
            ),
            priceGroups: Object.fromEntries(
                Object.entries(seatStates.value).map(([name, s]) => [name, s.priceGroups]),
            ),
        };
        localStorage.setItem('seatmapState', JSON.stringify(state));
    }

    function loadSeatmapStateFromStorage(): StoredSeatmapState | null {
        try {
            const raw = localStorage.getItem('seatmapState');
            if (!raw) return null;
            return JSON.parse(raw) as StoredSeatmapState;
        } catch {
            return null;
        }
    }

    /**
     * Saves the current seatmap selection (seatsOurs + seatTicketAssignments)
     * for all maps in the queue to an in-memory cache and localStorage so that
     * backwards navigation in the doorsale flow can restore the confirmed seats
     * without re-releasing the server-side reservation.
     */
    function saveDoorsaleEventSnapshot(eventId: number | string) {
        const seatsOurs: Record<string, number[]> = {};
        const assignments: Record<string, Record<number, number>> = {};
        const priceGroups: Record<string, Record<string, Record<string, SeatPriceGroupData>> | undefined> = {};
        for (const item of seatmapQueue.value) {
            seatsOurs[item.name] = [...(seatStates.value[item.name]?.seatsOurs ?? [])];
            assignments[item.name] = { ...(seatTicketAssignments.value[item.name] ?? {}) };
            priceGroups[item.name] = seatStates.value[item.name]?.priceGroups;
        }
        const snapshot: DoorsaleEventSeatSnapshot = { seatsOurs, seatTicketAssignments: assignments, priceGroups };
        doorsaleEventSnapshots.value[eventId] = snapshot;
        try {
            localStorage.setItem(`seatmapDoorsale_${eventId}`, JSON.stringify(snapshot));
        } catch { /* ignore quota errors */ }
    }

    function loadDoorsaleEventSnapshot(eventId: number | string): DoorsaleEventSeatSnapshot | null {
        if (doorsaleEventSnapshots.value[eventId]) return doorsaleEventSnapshots.value[eventId];
        try {
            const raw = localStorage.getItem(`seatmapDoorsale_${eventId}`);
            if (!raw) return null;
            const parsed = JSON.parse(raw) as DoorsaleEventSeatSnapshot;
            doorsaleEventSnapshots.value[eventId] = parsed;
            return parsed;
        } catch { return null; }
    }

    /**
     * Returns true if a confirmed doorsale seat snapshot exists for the given
     * event and ticket counts haven't changed since it was saved. Used by
     * useDoorsaleNavigation to keep the seats step active on revisits even
     * when seatmapActive is momentarily false (stale after clearCheckoutData).
     */
    function hasDoorsaleEventSnapshot(eventId: number | string): boolean {
        return loadDoorsaleEventSnapshot(eventId) != null;
    }

    function clearSeatmapState() {
        localStorage.removeItem('seatmapState');
        localStorage.removeItem('seatmapCompletedEvents');
        seatmapQueue.value = [];
        currentSeatmapIndex.value = 0;
        seatTicketAssignments.value = {};
        seatStates.value = {};
        completedEventSeatData.value = {};
        // Keep eventsWithSeatmaps intact — which events *have* seatmaps
        // doesn't change when ticket counts change. Only clear completion
        // tracking so users must re-select seats.
        eventsWithSeatsCompleted.value = {};
        // Invalidate the doorsale snapshot only for the event whose ticket counts
        // just changed. Other events' snapshots survive so backwards navigation
        // can still restore their confirmed seats.
        const checkoutStoreForClear = useCheckoutStore();
        const currentEventId = checkoutStoreForClear.getCheckoutID();
        if (currentEventId != null) {
            delete doorsaleEventSnapshots.value[currentEventId];
            localStorage.removeItem(`seatmapDoorsale_${currentEventId}`);
        }
    }

    /**
     * Fully clears the event seatmap registry (both tracking maps and
     * localStorage). Call this only on full cart reset.
     */
    function clearEventSeatmapRegistry() {
        eventsWithSeatmaps.value = {};
        eventsWithSeatsCompleted.value = {};
        completedEventSeatData.value = {};
        localStorage.removeItem('seatmapCompletedEvents');
        localStorage.removeItem('seatmapEventsRegistry');
        // Clear all per-event doorsale snapshots on full cart reset
        for (const eventId of Object.keys(doorsaleEventSnapshots.value)) {
            localStorage.removeItem(`seatmapDoorsale_${eventId}`);
        }
        doorsaleEventSnapshots.value = {};
    }

    function saveEventSeatmapRegistry() {
        localStorage.setItem('seatmapEventsRegistry', JSON.stringify({
            eventsWithSeatmaps: Object.keys(eventsWithSeatmaps.value),
        }));
    }

    function restoreEventSeatmapRegistry() {
        try {
            const raw = localStorage.getItem('seatmapEventsRegistry');
            if (!raw) return;
            const data = JSON.parse(raw) as { eventsWithSeatmaps: (string | number)[] };
            for (const id of data.eventsWithSeatmaps) {
                eventsWithSeatmaps.value[id] = true;
            }
        } catch { /* ignore corrupt data */ }
    }

    /**
     * Restores seatmapQueue, currentSeatmapIndex, and seatTicketAssignments from
     * localStorage if the stored state matches the given eventId.
     * Returns true when state was restored, false otherwise.
     */
    function restoreSeatmapState(eventId: number | string): boolean {
        const stored = loadSeatmapStateFromStorage();
        if (!stored || String(stored.eventId) != String(eventId)) return false;
        if (!stored.queue || stored.queue.length == 0) return false;

        seatmapQueue.value = stored.queue;
        currentSeatmapIndex.value = stored.currentSeatmapIndex ?? 0;
        seatTicketAssignments.value = stored.seatTicketAssignments ?? {};

        // Pre-populate seatStates with stored seatsOurs and priceGroups so that
        // isCurrentMapComplete is true and seat→ticket assignments resolve
        // immediately on mount — before the WS reserveseats response arrives.
        // The WS response will replace this with the authoritative server state.
        if (stored.seatsOurs) {
            const freshStates: Record<string, SeatState> = {};
            for (const item of stored.queue) {
                freshStates[item.name] = {
                    state2: [],
                    seatsOurs: stored.seatsOurs[item.name] ?? [],
                    colors: [],
                    overrideColors: [],
                    priceGroups: stored.priceGroups?.[item.name],
                };
            }
            seatStates.value = freshStates;
        }

        // Kick off asset loading for all maps in the restored queue
        for (const item of stored.queue) {
            loadSeatmap(item.name);
        }
        return true;
    }

    // Persist whenever the structural seatmap state changes (including seatsOurs
    // so the Continue button is immediately enabled on reload)
    watch(
        [seatmapQueue, currentSeatmapIndex, seatTicketAssignments, seatStates],
        () => {
            if (seatmapQueue.value.length == 0) return;
            const checkoutStore = useCheckoutStore();
            const eventId = checkoutStore.getCheckoutID();
            if (eventId != null) {
                storeSeatmapState(eventId);
            }
        },
        { deep: true },
    );

    /**
     * Returns a human-readable label for a seat index on the given seatmap, e.g.
     * "Row 3 · Seat 12". Falls back to the raw index string if data is unavailable.
     */
    function getSeatLabel(seatIndex: number, name: string): string {
        const seatmapData = cachedSeatmaps.value[name];
        if (!seatmapData) return String(seatIndex);
        const seat = seatmapData.seats[seatIndex];
        if (!seat) return String(seatIndex);
        const parts: string[] = [];
        if (seatmapData.fields[0] && seat.row) parts.push(`${seatmapData.fields[0].displayLabel} ${seat.row}`);
        if (seatmapData.fields[1] && seat.seatNumber) parts.push(`${seatmapData.fields[1].displayLabel} ${seat.seatNumber}`);
        return parts.length ? parts.join(' · ') : String(seatIndex);
    }

    return {
        cachedSeatmaps,
        loadedSeatmaps,
        seatmapQueue,
        currentSeatmapIndex,
        seatStates,
        seatmapMaxDistance,
        validatePriceGroupsOk,
        currentQueueItem,
        allSeatsSelected,
        loadSeatmap,
        advanceToNextSeatmap,
        sendEventseatmaps,
        sendReserveSeats,
        validateAndSendReserveSeats,
        resetCurrentSeatmap,
        setMaxDistance,
        handleEventseatmapsResponse,
        handleReserveSeatsResponse,
        getSeatLabel,
        getSeatPriceGroup,
        getEligibleTicketsForSeat,
        getAssignedSeatPriceGroup,
        seatTicketAssignments,
        assignTicketToSeat,
        swapSeatAssignments,
        buildPriceGroupInfo,
        buildAllPriceGroupInfo,
        snapshotCompletedEvent,
        sendValidatePriceGroups,
        handleValidatePriceGroupsResponse,
        restoreSeatmapState,
        clearSeatmapState,
        clearEventSeatmapRegistry,
        saveDoorsaleEventSnapshot,
        hasDoorsaleEventSnapshot,
        restoreEventSeatmapRegistry,
        registerEventWithSeatmaps,
        eventsWithSeatmaps,
        eventsWithSeatsCompleted,
        markEventSeatsCompleted,
        getNextEventNeedingSeats,
    };
});